import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Send, Bell, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../config/api';

export default function UrgentNotificationModal({ clientId, clientName, onClose }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isMobile) { setKeyboardHeight(0); return; }
    const viewport = window.visualViewport;
    if (!viewport) return;
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    return () => {
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isMobile]);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Veuillez entrer un message');
      return;
    }
    const token = localStorage.getItem('client_token');
    setSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/notifications/urgent?user_id=${clientId}&message=${encodeURIComponent(message)}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        toast.success('Notification urgente envoyée !');
        onClose();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-[10002] flex flex-col bg-white"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-4 border-b border-gray-100 bg-white"
            style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
          >
            <button onClick={onClose} className="p-2 -ml-2 text-gray-500">
              <ArrowLeft size={22} />
            </button>
            <div className="flex items-center gap-2 flex-1">
              <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={14} className="text-red-600" />
              </div>
              <h1 className="text-[15px] font-bold text-gray-900">Notification urgente</h1>
            </div>
          </div>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <p className="text-sm text-gray-500">
              Vers <strong className="text-gray-800">{clientName}</strong> — utilisé uniquement pour les situations nécessitant une action rapide.
            </p>

            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Message urgent
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ex: Besoin de ta validation pour le devis Dupont avant 17h..."
                className="w-full p-4 rounded-xl border border-gray-200 text-[15px] resize-none bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={5}
                autoFocus
                data-testid="urgent-message-input"
              />
            </div>

            <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
              <div className="flex items-start gap-2">
                <Bell size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Le client recevra une notification push et un email (si activé dans ses préférences).
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="p-4 border-t border-gray-100 bg-white"
            style={{
              paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
              marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px',
            }}
          >
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]"
              >
                Annuler
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !message.trim()}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold shadow-lg shadow-red-600/25 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="send-urgent-btn"
              >
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                Envoyer l'alerte
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10002] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          data-testid="urgent-notification-modal"
        >
          {/* Header */}
          <div className="bg-red-50 px-6 py-4 border-b border-red-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-900">Notification urgente</h3>
                  <p className="text-sm text-red-600">Vers {clientName}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-red-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-red-600" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            <p className="text-sm text-slate-600 mb-4">
              Cette notification sera mise en avant et le client recevra une alerte immédiate.
              Utilise cette fonction uniquement pour les situations qui nécessitent une action rapide.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Message urgent
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ex: Besoin de ta validation pour le devis Dupont avant 17h..."
                  className="w-full p-4 rounded-xl border border-gray-200 text-[15px] resize-none bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 h-32"
                  data-testid="urgent-message-input"
                />
              </div>
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                <div className="flex items-start gap-2">
                  <Bell className="w-4 h-4 text-amber-600 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Le client recevra une notification push et un email (si activé dans ses préférences).
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]"
            >
              Annuler
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="send-urgent-btn"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
              Envoyer l'alerte
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
